// NATIVE
import React, { useEffect, useState } from 'react';

// INSTALLED
import Box from '@mui/material/Box';
import { Formik, Form } from 'formik';
import Step from '@mui/material/Step';
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Divider } from '@mui/material';

// CUSTOM
import { editSucursal } from '../../store/sucursal';
import { SnackAlert } from '../../components/SnackAlert';
import MultipleSnackbars from '../../components/MultipleSnackbars';
import { mtDividerUpTable, timeToClosedToast } from '../../commons';
import { closeSessionGlobal, hasPemissions } from '../../commons/utils';
import { EditSucursalEmpresa } from '../../components/sucursal/EditSucursalEmpresa';
import { BreadCrumbsAndTitle } from '../../components/generics/BreadCrumbsAndTitle';
import { ModalSessionExpired } from '../../components/generics/ModalSessionExpired';
import validationSchema from '../../components/sucursal/formModels/edit/validationSchema';
import formInitialValues from '../../components/sucursal/formModels/edit/editSucursalFormModel';
import editSucursalFormModel from '../../components/sucursal/formModels/edit/editSucursalFormModel';

/**
 * EditarSucursal component allows users to edit a branch (Sucursal) within the application.
 *
 * This component utilizes various hooks and Redux state to manage the form state, handle form submission,
 * and display appropriate messages and alerts based on the response from the server.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <EditarSucursal />
 *
 * @remarks
 * - The component uses `useDispatch` and `useSelector` from Redux to manage state.
 * - It uses `Formik` for form handling and validation.
 * - The component handles different response codes (401, 403, 409, 400) to display appropriate messages.
 * - It includes a stepper to guide the user through the editing process.
 *
 * @requires
 * - `useDispatch` from 'react-redux'
 * - `useSelector` from 'react-redux'
 * - `React` from 'react'
 * - `useState` from 'react'
 * - `useEffect` from 'react'
 * - `Box` from '@mui/material'
 * - `ModalSessionExpired` from 'components/ModalSessionExpired'
 * - `MultipleSnackbars` from 'components/MultipleSnackbars'
 * - `SnackAlert` from 'components/SnackAlert'
 * - `BreadCrumbsAndTitle` from 'components/BreadCrumbsAndTitle'
 * - `Divider` from '@mui/material'
 * - `Stepper` from '@mui/material'
 * - `Step` from '@mui/material'
 * - `StepLabel` from '@mui/material'
 * - `Formik` from 'formik'
 * - `Card` from '@mui/material'
 * - `Form` from 'formik'
 * - `Button` from '@mui/material'
 */
export const EditarSucursal = () => {
  // USE DISPATCH
  const dispatch = useDispatch();

  // REDUX
  const { departaments_list }: any = useSelector((state: any) => state.departaments);
  const { municipaly_list }: any = useSelector((state: any) => state.municipalies);

  // FORM MODEL DATA
  const { formId, formField } = editSucursalFormModel;

  // USE STATE
  const [msgSnack, setMsgSnack] = React.useState({
    msg: '',
    status: 'success',
  });

  const [timeAlert, setTimeAlert] = useState(timeToClosedToast);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [btnText, setBtnText] = React.useState('Editar Sucursal');

  const [messagesAlerts, setMessagesAlerts] = useState<any>([]);
  const [modalSessionExpired, setModalSessionExpired] = React.useState(false);

  const [activeStep, setActiveStep] = useState(0);
  const [alert_snack, setAlertSnack] = useState(false);
  const [departaments, setDepartaments] = useState([]);
  const [municipalies, setMunicipalies] = useState([]);
  const [percentaje_c_Company, setPercentajeSucursal] = useState(0);

  // CONST VALUES
  const steps: string[] = ['Editar Sucursal'];

  const isLastStep = activeStep === steps.length - 1;

  const currentValidationSchema = validationSchema[activeStep];

  // USE EFFECT
  useEffect(() => {
    setDepartaments(departaments_list);
  }, [departaments_list]);

  useEffect(() => {
    setMunicipalies(municipaly_list);
  }, [municipaly_list]);

  // CONST FUNCTIONS
  const _renderStepContent = (steps: any) => {
    switch (steps) {
      case 0:
        return <EditSucursalEmpresa formField={formField} />;
    }
  };

  const _submitForm = async (values: any, actions: any) => {
    console.log('kekekekekkekekekek');

    const havePermissions = await hasPemissions('modulo.configuracion', 'modulo.sucursal', 'edit');

    if (!havePermissions) {
      await validateLsPermissions('No tienes pemisos para (Editar) sucursal.');

      return false;
    }

    setIsSubmitting(true);
    setBtnText('Un momento...');

    actions.setSubmitting(true);

    const response = await dispatch(editSucursal(values, setPercentajeSucursal));

    setIsSubmitting(false);
    setBtnText('Editar Sucursal');

    const result = await validateResponseEdit(response, ' (Editar Sucursal.) ');

    if (!result) {
      return false;
    }

    if (response.ok) {
      const resp: any = {
        msg: response.message,
        status: 'success',
      };

      await setMsgSnack(resp);
      await setAlertSnack(true);
    }
  };

  const validateResponseEdit = async (response: any, textActionConcat: string) => {
    if (response.code == 401) {
      await localStorage.removeItem('lastSelectedQypht');
      await closeSessionGlobal();
      setModalSessionExpired(true);
      return false;
    }

    // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION
    if (!response.ok && response.code == 403) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 403 EL DATO YA SE ENCUENTRA REGISTRADO
    if (!response.ok && response.code == 409) {
      const errors: any = {
        msg: response.errors[0] + ' ' + textActionConcat,
        status: 'error',
      };

      await setMsgSnack(errors);
      await setAlertSnack(true);

      return false;
    }

    // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO
    if (!response.ok && response.code == 400) {
      const errors: any = [];

      response.errors.forEach((element: any) => {
        errors.push({
          type: 'error',
          text: element,
        });
      });

      setMessagesAlerts(errors);
      return false;
    }

    return true;
  };

  const validateLsPermissions = async (message: string) => {
    const errors: any = {
      msg: message,
      status: 'error',
    };

    await setMsgSnack(errors);
    await setAlertSnack(true);
  };

  const _handleSubmit = (values: any, actions: any) => {
    _submitForm(values, actions);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <ModalSessionExpired open={modalSessionExpired} />

      <MultipleSnackbars
        messagesList={messagesAlerts}
        position={'bottom-left'}
        autoClose={timeAlert}
      />

      <SnackAlert
        open={alert_snack}
        onclick={setAlertSnack}
        text={msgSnack.msg}
        severity={msgSnack.status}
      />

      <BreadCrumbsAndTitle
        title="Editar Sucursal"
        mainModule="configuracion"
        parent="Listar Sucursales"
        action="/configuracion/sucursales"
      />

      <Divider sx={{ marginBottom: mtDividerUpTable }} />

      <Stepper activeStep={activeStep}>
        {steps.map((label: any, index: any) => {
          const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
          } = {};

          return (
            <Step key={label}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Formik
        initialValues={formInitialValues}
        validationSchema={currentValidationSchema}
        onSubmit={_handleSubmit}
      >
        <Card style={{ marginTop: 50, padding: 20 }}>
          <Form id={formId}>
            {_renderStepContent(activeStep)}

            <div style={{ display: 'flex', justifyContent: 'end', marginTop: 25 }}>
              <Button
                disabled={isSubmitting}
                style={{ margin: 5 }}
                type="submit"
                variant="contained"
                color={'success'}
              >
                {btnText}
              </Button>
            </div>
          </Form>
        </Card>
      </Formik>
    </Box>
  );
};
